import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import PropTypes from "prop-types";
import { Typography, Button, Box, Grid, InputAdornment } from "@mui/material";
import Modal from "../../../../components/Modal/Modal";
import { useTranslation } from "react-i18next";
import Table from "../../../../components/Table/Table";
import ButtonGroup from "../../../../components/Buttons/ButtonGroup";
import { useCourses } from "../../../../hooks/courseBuilder";
import CourseTypeIcon from "../../../../components/Icons/CourseTypeIcon";
import CourseFlags from "../../../../components/Icons/CourseFlags";
import { fetchCoursesFilterData } from "../../../../redux/actions/contentCoursesActions";
import PackagesFilter from "../../../../components/FormikFields/Filters/PackagesFilter";
import Select from "../../../../components/FormikFields/Select";
import SearchIcon from "@mui/icons-material/Search";
import { TextField } from "formik-mui";
import LoadingButton from "../../../../components/Buttons/LoadingButton";

const getColumns = (includePolicies, hideTotalActivitiesColumn) => {
  let columns = [
    {
      title: "Type",
      field: "courseType",
      options: {
        sorting: true
      },
      cellStyle: {
        padding: "8px 12px 0 0",
        width: 30,
        textAlign: "center"
      },
      headerStyle: {
        paddingLeft: 0
      },
      render: ({ courseType }) => <CourseTypeIcon type={courseType} />,
      customSort: () => 0
    },
    {
      title: "Name",
      field: "name",
      options: {
        sorting: true
      },
      cellStyle: {
        paddingLeft: 0,
        minWidth: 320
      },
      headerStyle: {
        paddingLeft: 0
      },
      render: ({ name, flags }) => (
        <Box display="flex" alignItems="center">
          <Box mr={2}>{name}</Box>
          {flags && <CourseFlags flags={flags} />}
        </Box>
      )
    },
    {
      title: includePolicies ? "coursePackageTitle" : "package",
      field: "package",
      options: {
        sorting: true
      },
      cellStyle: {
        width: 320
      },
      render: ({ packages }) =>
        packages ? packages.map(({ name }) => name).join(", ") : ""
    }
  ];

  if (!hideTotalActivitiesColumn) {
    columns.push({
      title: "totalActivities",
      field: "totalActivities",
      cellStyle: {
        minWidth: 180
      }
    });
  }

  return columns;
};

const AddCourseFilters = ({
  onFilter,
  includePolicies,
  modalOpen,
  resetForm
}) => {
  const { t } = useTranslation();
  const trainingTypeFilterOptions = [
    {
      value: "",
      label: t("filterLabels.showAll")
    },
    { value: "online", label: t("filterLabels.online") },
    { value: "ilt", label: t("filterLabels.ilt") }
  ];
  if (includePolicies) {
    trainingTypeFilterOptions.push({
      value: "policy",
      label: t("filterLabels.policy")
    });
  }

  useEffect(() => {
    if (!modalOpen) {
      resetForm();
    }
  }, [modalOpen]);

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} md={12} lg={10}>
        <Field
          label={t("addCourse.searchLabel")}
          name="searchInput"
          variant="filled"
          fullWidth
          component={TextField}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            disableUnderline: true
          }}
        />
      </Grid>
      <Grid
        item
        md={0}
        lg={2}
        sx={{ display: { xs: "none", md: "none", lg: "block" } }}
      />
      <Grid item xs={12} sm={6} md={6} lg={5}>
        <PackagesFilter packageType={includePolicies ? "all" : "course"} />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <Select
          disableUnderline
          name="courseType"
          menuItems={trainingTypeFilterOptions}
          label={t("addCourse.filterByTypeLabel")}
        />
      </Grid>
      <Grid item xs={12} md={12} lg={2} sx={{ alignSelf: "center" }}>
        <ButtonGroup align="right">
          <Button color="primary" onClick={() => resetForm()}>
            {t("addCourse.clearBtn")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            className="wide"
            onClick={() => onFilter()}
          >
            {t("addCourse.filterBtn")}
          </Button>
        </ButtonGroup>
      </Grid>
    </Grid>
  );
};
const AddCourseModal = ({
  onClose,
  open,
  isSubmitting,
  onFilter,
  onAddCourses,
  items,
  itemsTableRef,
  includePolicies,
  hideTotalActivitiesColumn,
  getRowId
}) => {
  const { t } = useTranslation();

  const [selectedRows, setSelectedRows] = useState([]);

  const handleSelectedRowsChange = rows => {
    setSelectedRows(Object.keys(rows).filter(selected => rows[selected]));
  };

  return (
    <Modal
      scrollable
      disableEnforceFocus
      showCloseIcon
      maxWidth="lg"
      fullWidth
      fillHeight
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t("addCourse.modalTitle")}
        </Typography>
      }
      dialogContent={
        <Box display="flex" flexDirection="column" mb={1}>
          <Formik
            initialValues={{
              searchInput: "",
              contentPackage: "",
              courseType: ""
            }}
            onSubmit={(values, { setSubmitting }) => {
              onFilter({ ...values });
              setSubmitting(false);
            }}
          >
            {({ submitForm, resetForm }) => (
              <Form>
                <AddCourseFilters
                  resetForm={resetForm}
                  onFilter={submitForm}
                  modalOpen={open}
                  includePolicies={includePolicies}
                />
              </Form>
            )}
          </Formik>
          <Table
            onSelectionChange={handleSelectedRowsChange}
            tableRef={itemsTableRef}
            columns={getColumns(includePolicies, hideTotalActivitiesColumn)}
            selection={true}
            selectionCellStyle={{
              border: "none",
              width: 70,
              textAlign: "center"
            }}
            data={items}
            filtering={false}
            rowClickDisabled={item => item.disabled}
            rowSelectionDisabled={item => item.disabled}
            getRowId={getRowId}
          />
        </Box>
      }
      dialogActions={
        <>
          <Button color="primary" disabled={isSubmitting} onClick={onClose}>
            {t("addCourse.cancelBtn")}
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={() => onAddCourses(selectedRows, setSelectedRows)}
            disabled={selectedRows.length === 0}
            pending={isSubmitting}
          >
            {t("addCourse.addCourseBtn")}
          </LoadingButton>
        </>
      }
      open={open}
      onClose={onClose}
    />
  );
};

const AddCourse = ({
  open,
  onClose,
  includePolicies,
  excludeChildTrainingObjects,
  excludedTrainingObjectIds,
  onAddCourses,
  isSubmitting,
  hideTotalActivitiesColumn,
  getRowId
}) => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState({});
  const { getCourses } = useCourses();
  const tableRef = useRef(null);

  useEffect(() => {
    dispatch(fetchCoursesFilterData("all"));
  }, []);

  useEffect(() => {
    if (!open) {
      setFilters({});
    }
  }, [open]);

  const updateTable = () =>
    tableRef.current && tableRef.current.onQueryChange();

  const fetchCourses = ({ orderBy, orderDirection, page, pageSize }) => {
    const orderByColumn = orderBy ? orderBy.field : null;
    const pullCourses = async () => {
      const modifiedFilters = {
        ...filters,
        parentTrainingObjectIdNull: excludeChildTrainingObjects,
        trainingObjectIdNotEq: excludedTrainingObjectIds
      };
      const courses = await getCourses({
        page,
        pageSize,
        orderDirection,
        orderBy: orderByColumn,
        filters: modifiedFilters
      });
      // Material Table Pagination Refactoring
      // Material Table requires 0 indexing
      courses.page -= 1;
      return courses;
    };

    return pullCourses();
  };

  const handleFilterSubmit = filters => {
    setFilters(filters);
    updateTable();
  };

  return (
    <AddCourseModal
      open={open}
      onAddCourses={onAddCourses}
      onClose={onClose}
      items={fetchCourses}
      onFilter={handleFilterSubmit}
      itemsTableRef={tableRef}
      includePolicies={includePolicies}
      hideTotalActivitiesColumn={hideTotalActivitiesColumn}
      isSubmitting={isSubmitting}
      getRowId={getRowId}
    />
  );
};

export default AddCourse;

AddCourse.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to add the selected courses to the destination training object
   */
  onAddCourses: PropTypes.func,
  includePolicies: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  hideTotalActivitiesColumn: PropTypes.bool,
  getRowId: PropTypes.func,
  excludedTrainingObjectIds: PropTypes.array
};

AddCourse.defaultProps = {
  includePolicies: false,
  courseFilters: {}
};
